import { z } from 'zod';

const StoredPhotoSchema = z.object({
  id: z.string(),
  dataUrl: z.string(),
  timestamp: z.string()
});

type StoredPhoto = z.infer<typeof StoredPhotoSchema>;

class StorageService {
  private static instance: StorageService;
  private readonly PHOTO_PREFIX = 'student_photo_';
  private readonly STORAGE_VERSION = '1.0';
  private readonly VERSION_KEY = 'storage_version';

  private constructor() {
    this.initializeStorage();
  }

  private initializeStorage(): void {
    try {
      // Check storage version
      const version = localStorage.getItem(this.VERSION_KEY);
      if (version !== this.STORAGE_VERSION) {
        // Clear storage if version mismatch or not set
        this.clearAllPhotos();
        localStorage.setItem(this.VERSION_KEY, this.STORAGE_VERSION);
      }

      // Validate existing photos
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.PHOTO_PREFIX)) {
          try {
            const data = JSON.parse(localStorage.getItem(key) || '');
            StoredPhotoSchema.parse(data);
          } catch (error) {
            // Remove invalid data
            localStorage.removeItem(key);
          }
        }
      });
    } catch (error) {
      console.error('Storage initialization error:', error);
      // Reset storage in case of critical error
      this.clearAllPhotos();
      localStorage.setItem(this.VERSION_KEY, this.STORAGE_VERSION);
    }
  }

  public static getInstance(): StorageService {
    if (!StorageService.instance) {
      StorageService.instance = new StorageService();
    }
    return StorageService.instance;
  }

  public async savePhoto(studentId: string, file: File): Promise<string> {
    try {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = () => {
          try {
            const dataUrl = reader.result as string;
            const photoData: StoredPhoto = {
              id: studentId,
              dataUrl,
              timestamp: new Date().toISOString()
            };
            
            // Validate before storing
            StoredPhotoSchema.parse(photoData);
            localStorage.setItem(
              this.PHOTO_PREFIX + studentId, 
              JSON.stringify(photoData)
            );
            
            resolve(dataUrl);
          } catch (error) {
            reject(new Error('Failed to save photo to storage'));
          }
        };
        
        reader.onerror = () => {
          reject(new Error('Failed to read file'));
        };

        reader.readAsDataURL(file);
      });
    } catch (error) {
      console.error('Error saving photo:', error);
      throw error;
    }
  }

  public getPhoto(studentId: string): string | null {
    try {
      const data = localStorage.getItem(this.PHOTO_PREFIX + studentId);
      if (!data) return null;

      const photoData = JSON.parse(data);
      StoredPhotoSchema.parse(photoData);
      return photoData.dataUrl;
    } catch (error) {
      console.error('Error getting photo:', error);
      // Remove invalid data
      localStorage.removeItem(this.PHOTO_PREFIX + studentId);
      return null;
    }
  }

  public removePhoto(studentId: string): void {
    try {
      localStorage.removeItem(this.PHOTO_PREFIX + studentId);
    } catch (error) {
      console.error('Error removing photo:', error);
    }
  }

  public async replacePhoto(studentId: string, file: File): Promise<string> {
    try {
      // Remove existing photo first
      this.removePhoto(studentId);
      // Save and return new photo
      return await this.savePhoto(studentId, file);
    } catch (error) {
      console.error('Error replacing photo:', error);
      throw error;
    }
  }

  public clearAllPhotos(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.PHOTO_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error clearing photos:', error);
    }
  }
}

export const storageService = StorageService.getInstance();